import { useLocation, Link } from 'react-router-dom';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useSidebar } from '../contexts/SidebarContext';
import ThemeToggle from './ThemeToggle';
import { 
  FileText, 
  Users, 
  MessageSquare, 
  Calendar as CalendarIcon, 
  LogOut,
  Menu,
  X,
  BarChart3
} from 'lucide-react';
import { toast } from 'sonner';

const Sidebar = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { isCollapsed, isMobileOpen, toggleSidebar, toggleMobileSidebar, closeMobileSidebar } = useSidebar();
  
  const navItems = [
    { path: '/', label: 'Dashboard', icon: BarChart3 },
    { path: '/pupils', label: 'Pupils', icon: Users },
    { path: '/questions', label: 'Questions', icon: MessageSquare },
    { path: '/logs', label: 'Reflection Logs', icon: CalendarIcon }
  ];

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={closeMobileSidebar}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed top-0 left-0 h-full bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 z-40
        transition-all duration-300 ease-in-out
        ${isCollapsed ? 'w-16' : 'w-64'}
        ${isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className={`flex items-center space-x-3 transition-opacity duration-300 ${isCollapsed ? 'opacity-0' : 'opacity-100'}`}>
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">RL</span>
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-800 dark:text-gray-200">ReflectLogs</h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">Instructor Portal</p>
            </div>
          </div>
          
          {/* Desktop Toggle Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleSidebar}
            className="hidden lg:flex"
          >
            {isCollapsed ? <Menu size={16} /> : <X size={16} />}
          </Button>

          {/* Mobile Close Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={closeMobileSidebar}
            className="lg:hidden"
          >
            <X size={16} />
          </Button>
        </div>

        {/* Navigation Items */}
        <nav className="flex-1 p-4">
          <div className="space-y-2">
            {navItems.map(({ path, label, icon: Icon }) => {
              const isActive = location.pathname === path;
              return (
                <Link key={path} to={path} onClick={closeMobileSidebar}>
                  <Button
                    variant={isActive ? "default" : "ghost"}
                    className={`
                      w-full justify-start transition-all duration-300
                      ${isCollapsed ? 'px-2' : 'px-3'}
                      ${isActive ? 'bg-blue-600 text-white hover:bg-blue-700' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}
                    `}
                  >
                    <Icon size={20} className={isCollapsed ? 'mx-auto' : 'mr-3'} />
                    <span className={`transition-opacity duration-300 ${isCollapsed ? 'opacity-0 w-0' : 'opacity-100'}`}>
                      {label}
                    </span>
                  </Button>
                </Link>
              );
            })}
          </div>
        </nav>

        {/* User Info & Actions */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          {/* User Info */}
          <div className={`mb-4 transition-opacity duration-300 ${isCollapsed ? 'opacity-0' : 'opacity-100'}`}>
            <div className="flex items-center space-x-3 p-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                <span className="text-blue-600 dark:text-blue-300 font-medium text-sm">
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                  {user?.name || 'User'}
                </p>
                {user?.school_name && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {user.school_name}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Theme Toggle & Logout */}
          <div className="space-y-2">
            <div className={`flex ${isCollapsed ? 'justify-center' : 'justify-between items-center'}`}>
              <ThemeToggle 
                variant="outline" 
                size="sm"
                className={isCollapsed ? 'w-full' : ''}
              />
              {!isCollapsed && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleLogout}
                  className="flex items-center space-x-2"
                >
                  <LogOut size={16} />
                  <span>Logout</span>
                </Button>
              )}
            </div>
            
            {isCollapsed && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                className="w-full"
              >
                <LogOut size={16} />
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;